/**
 * cascadePreview — QUESTIONNAIRE-2.0-A-S2-γ-A (helper puro, stand-alone).
 *
 * Pure functions to compute the impact preview of a base-language value
 * edit BEFORE it is submitted via PATCH /i18n. No fetch. No DOM. No mutation.
 *
 * Contract recap (analysis γ §10):
 *   When `value_changed && is_base_lang(lang)` is true server-side, every
 *   non-base lang sibling path whose `translation_status` is "reviewed"
 *   gets demoted to "needs_update".
 *
 * `computeCascadePreview` mirrors that logic client-side, read-only, against
 *  the in-memory `translation_status` map + the summary languages payload.
 *
 * Out of scope for γ-A:
 *   - Wire into QuestionnaireBuilder.
 *   - Fetch the doc / summary.
 *   - Persist anything.
 */

// Mongo convention: hyphenated language codes use underscores (`pt-br` → `pt_br`).
export const normLang = (code) => (code || "").replace("-", "_");

// Reverse: build a "summary-friendly" canonical code from the normalized one
// when the summary has it. `pt_br` → `pt-br`.
export const denormLang = (norm) => (norm || "").replace("_", "-");

// Detect if a code is one of the three base authoring languages.
const BASE_LANG_NORMS = new Set(["en", "es", "zh"]);
export const isBaseLangCode = (code) => BASE_LANG_NORMS.has(normLang(code));

/**
 * Parse the *base* dotted-path and extract `{ stem, baseLangNorm }` so we
 * can rebuild siblings for every non-base lang.
 *
 * Accepted shapes (mirror of pathFor in QuestionnaireBuilder.jsx):
 *   `title_en`                                      → { stem: "title",                                baseLangNorm: "en" }
 *   `description_es`                                → { stem: "description",                          baseLangNorm: "es" }
 *   `questions.QID.label_zh`                        → { stem: "questions.QID.label",                  baseLangNorm: "zh" }
 *   `questions.QID.help_en`                         → { stem: "questions.QID.help",                   baseLangNorm: "en" }
 *   `questions.QID.section_es`                      → { stem: "questions.QID.section",                baseLangNorm: "es" }
 *   `questions.QID.options.VAL.label_zh`            → { stem: "questions.QID.options.VAL.label",      baseLangNorm: "zh" }
 *
 * Returns null when the path tail does not end on a base lang suffix.
 */
export const parseBasePath = (basePath) => {
  if (!basePath || typeof basePath !== "string") return null;
  const m = basePath.match(/^(.+)_(en|es|zh)$/);
  if (!m) return null;
  return { stem: m[1], baseLangNorm: m[2] };
};

/**
 * Build the sibling path for `(stem, targetLangCode)`. The lang code may be
 * `pt-br` (hyphen) or `pt_br` (underscore); we normalize to the underscore
 * form to match how the doc stores keys.
 */
export const buildSiblingPath = (stem, targetLangCode) =>
  `${stem}_${normLang(targetLangCode)}`;

/**
 * computeCascadePreview — main entry point.
 *
 * @param {object}   params
 * @param {object}   params.translation_status  Flat map { "title_fr": "reviewed", ... }
 * @param {string}   params.basePath            "title_en" | "questions.X.label_es" | ...
 * @param {object}   params.summary             Same shape returned by /translation-summary
 *                                              (used for canonical language_code, direction,
 *                                              language_name_native, has_scaffold).
 * @returns {{ count: number, langs: Array, applicable: boolean, reason?: string }}
 *   - `applicable=false` when `basePath` is not a base-lang field (γ-A is a
 *     no-op preview for non-base paths). Callers should hide the dialog or
 *     skip the cascade preview in that case.
 *   - `langs` is sorted: RTL first (visual prominence), then alpha by code.
 */
export const computeCascadePreview = ({ translation_status, basePath, summary }) => {
  const parsed = parseBasePath(basePath);
  if (!parsed) {
    return { count: 0, langs: [], applicable: false, reason: "not_base_path" };
  }
  if (!summary || !Array.isArray(summary.languages)) {
    return { count: 0, langs: [], applicable: true, reason: "no_summary" };
  }
  const ts = translation_status || {};
  const { stem, baseLangNorm } = parsed;

  const affected = [];
  for (const L of summary.languages) {
    if (L.is_base) continue;                          // EN/ES/ZH excluded
    if (normLang(L.language_code) === baseLangNorm) continue;  // self
    const sibling = buildSiblingPath(stem, L.language_code);
    if (ts[sibling] === "reviewed") {
      affected.push({
        language_code: L.language_code,
        language_name_native: L.language_name_native,
        direction: L.direction || "ltr",
        sibling_path: sibling,
      });
    }
  }

  // Sort: RTL first, then alpha.
  affected.sort((a, b) => {
    if (a.direction === "rtl" && b.direction !== "rtl") return -1;
    if (a.direction !== "rtl" && b.direction === "rtl") return 1;
    return a.language_code.localeCompare(b.language_code);
  });

  return { count: affected.length, langs: affected, applicable: true };
};

/**
 * Compute the warning level + UI metadata for a cascade preview count.
 *
 * Bands (per γ analysis §9.4):
 *   0     → "ok"     (verde · ✓ No reviewed translations affected)
 *   1-4   → "info"   (azul · ℹ informational)
 *   5-9   → "warn"   (amber · ⚠ consider batching edits)
 *   10+   → "danger" (rose · ⚠ strong recommendation to batch)
 */
export const computeWarningLevel = (count) => {
  if (count <= 0) {
    return {
      level: "ok",
      icon: "✓",
      tone: "emerald",
      message: "No reviewed translations affected.",
    };
  }
  if (count <= 4) {
    return {
      level: "info",
      icon: "ℹ",
      tone: "sky",
      message: `${count} reviewed translation(s) will need review.`,
    };
  }
  if (count <= 9) {
    return {
      level: "warn",
      icon: "⚠",
      tone: "amber",
      message: `${count} reviewed translation(s) will need review. Consider batching edits.`,
    };
  }
  return {
    level: "danger",
    icon: "⚠",
    tone: "rose",
    message: `${count} reviewed translation(s) will need review. Strong recommendation: batch all base edits before publishing.`,
  };
};

/**
 * Friendly label builder for the dialog header. Converts a dotted-path
 * into a human-readable trail:
 *   "title_en"                            → "Title · EN"
 *   "description_es"                      → "Description · ES"
 *   "questions.QID.label_zh"              → "Question · Label · ZH"
 *   "questions.QID.help_en"               → "Question · Help · EN"
 *   "questions.QID.section_es"            → "Question · Section · ES"
 *   "questions.QID.options.VAL.label_zh"  → "Question · Option · Label · ZH"
 *
 * Question and option identifiers are intentionally elided — the caller
 * already shows the question index in its parent container.
 */
export const friendlyPathLabel = (basePath) => {
  const parsed = parseBasePath(basePath);
  if (!parsed) return basePath || "";
  const { stem, baseLangNorm } = parsed;
  const langUpper = denormLang(baseLangNorm).toUpperCase();
  if (!stem.includes(".")) {
    return `${capitalize(stem)} · ${langUpper}`;
  }
  // questions.QID.<field>  or  questions.QID.options.VAL.<field>
  const segs = stem.split(".");
  if (segs.length >= 5 && segs[0] === "questions" && segs[2] === "options") {
    return `Question · Option · ${capitalize(segs[4])} · ${langUpper}`;
  }
  if (segs.length >= 3 && segs[0] === "questions") {
    return `Question · ${capitalize(segs[2])} · ${langUpper}`;
  }
  return `${capitalize(stem)} · ${langUpper}`;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const cascadePreviewModule = {
  computeCascadePreview,
  computeWarningLevel,
  friendlyPathLabel,
  parseBasePath,
  buildSiblingPath,
  normLang,
  denormLang,
  isBaseLangCode,
};

export default cascadePreviewModule;
